import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  RefreshCw, Plus, Tag, MoreHorizontal, Search, FileText, Briefcase, ListFilter,
  Printer, Download, Users,
} from "lucide-react";
import { toast } from "sonner";
import { formatName, fmtDate } from "@/lib/format";
import ProvisionReadyBadge from "@/components/ProvisionReadyBadge";
import * as XLSX from "xlsx";

/* ─────────────────────────── Status dots (hover for label) ─────────────────────────── */
function Dot({ color, title, testId }) {
  return (
    <span
      className={`inline-block w-2.5 h-2.5 rounded-full ${color} ring-1 ring-black/5 shadow-sm`}
      title={title}
      data-testid={testId}
      aria-label={title}
    />
  );
}

const POLICY_DOT_MAP = {
  bozza:                { dot: "bg-slate-500",   label: "Entwurf" },
  in_Revision:          { dot: "bg-amber-500",   label: "In Revision" },
  documenti_completi:   { dot: "bg-sky-500",     label: "Dokumente Komplett" },
  genehmigtto_control:  { dot: "bg-emerald-500", label: "Genehmigt (Mit Finma)" },
  attesa_compagnia:     { dot: "bg-indigo-500",  label: "Warten auf Gesellschaft" },
  attivo:               { dot: "bg-green-600",   label: "Aktiv" },
  pagato:               { dot: "bg-violet-600",  label: "Bezahlt" },
  stornato:             { dot: "bg-red-600",     label: "Storniert" },
};
function PolicyStatusDot({ status }) {
  const m = POLICY_DOT_MAP[status] || POLICY_DOT_MAP.bozza;
  return <Dot color={m.dot} title={m.label} testId={`policy-dot-${status}`} />;
}

const CUSTOMER_DOT_MAP = {
  neukunde:      { dot: "bg-emerald-500", label: "Neukunde" },
  bestandskunde: { dot: "bg-sky-500",     label: "Bestandskunde" },
  ex_kunde:      { dot: "bg-stone-500",   label: "Ex-Kunde" },
};
function CustomerStatusDot({ status }) {
  if (!status) return null;
  const m = CUSTOMER_DOT_MAP[status] || { dot: "bg-stone-400", label: status };
  return <Dot color={m.dot} title={m.label} testId={`customer-dot-${status}`} />;
}

function AntragStatusDot({ status, deadline }) {
  let color = "bg-amber-500", label = "In Bearbeitung";
  if (status === "approved")      { color = "bg-emerald-600"; label = "Genehmigt"; }
  else if (status === "rejected") { color = "bg-red-600";     label = "Abgelehnt"; }
  else {
    const overdue = deadline && new Date(deadline) < new Date(new Date().toISOString().slice(0, 10));
    if (overdue) { color = "bg-red-600"; label = "Frist überschritten"; }
  }
  return <Dot color={color} title={label} testId={`antrag-dot-${status}`} />;
}

const PAYMENT_DOT_MAP = {
  green:  { dot: "bg-emerald-500", label: "Wird im Monat bezahlt" },
  orange: { dot: "bg-amber-500",   label: "Akzeptiert, noch nicht erhalten" },
  red:    { dot: "bg-rose-500",    label: "Nicht bezahlt · nächster Monat" },
  grey:   { dot: "bg-stone-400",   label: "Noch nicht aktiv" },
};
function PaymentDot({ color }) {
  const m = PAYMENT_DOT_MAP[color] || PAYMENT_DOT_MAP.grey;
  return <Dot color={m.dot} title={m.label} testId={`payment-dot-${color}`} />;
}

/* ─────────────────────────── Payment color derivation ─────────────────────────── */
function derivePaymentColor(policy, commissionsForPolicy) {
  const status = policy.status;
  if (["bozza", "in_Revision", "documenti_completi", "genehmigtto_control", "attesa_compagnia", "stornato"].includes(status)) return "grey";
  const cs = commissionsForPolicy || [];
  const now = new Date();
  const cy = now.getUTCFullYear(), cm = now.getUTCMonth();
  const isCurMonth = (iso) => { if (!iso) return false; const d = new Date(iso); return d.getUTCFullYear() === cy && d.getUTCMonth() === cm; };
  if (cs.some((c) => isCurMonth(c.paid_at) || isCurMonth(c.released_at))) return "green";
  if (cs.length === 0) return "orange";
  if (cs.some((c) => c.status === "partially_paid" || c.status === "pending" || (c.paid_amount || 0) === 0)) return "red";
  return "grey";
}
const PAYMENT_LABEL_FOR_EXCEL = (color) => PAYMENT_DOT_MAP[color]?.label || "—";

/* ─────────────────────────── Formatters ─────────────────────────── */
const INSURANCE_TYPE_SHORT = {
  krankenkasse: "KV", unfall: "UVG", auto: "Auto", hausrat: "HH",
  haftpflicht: "Haftpfl.", rechtsschutz: "RS", motorrad: "MK",
  vita_3a: "Sparen-LV", vita_3b: "Sparen-LV", vita_risiko: "Risiko-LV",
  betrieblich: "Betrieb",
};
const fmtChf = (n) => {
  const x = Number(n || 0);
  if (!Number.isFinite(x)) return "0";
  return new Intl.NumberFormat("it-CH", { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(x);
};

/* ─────────────────────────── Client picker ─────────────────────────── */
function ClientPicker({ open, onClose, onPick, label }) {
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  useEffect(() => {
    if (!open) return;
    (async () => { try { const r = await api.get("/clients"); setList(r.data || []); } catch { /* ignore */ } })();
  }, [open]);
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return list.slice(0, 30);
    return list.filter((c) => `${c.first_name || ""} ${c.last_name || ""} ${c.email || ""}`.toLowerCase().includes(t)).slice(0, 30);
  }, [q, list]);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{label}</DialogTitle>
          <DialogDescription>Wählen Sie den Kunden für die neue Eintragung.</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Kunde suchen…" className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} data-testid="picker-search" autoFocus />
        </div>
        <div className="max-h-72 overflow-y-auto divide-y divide-border border border-border rounded-md">
          {filtered.length === 0
            ? <div className="p-6 text-center text-sm text-muted-foreground">Keine Treffer</div>
            : filtered.map((c) => (
              <button key={c.id} onClick={() => { onPick(c); onClose(); }}
                      className="w-full text-left px-4 py-3 hover:bg-secondary transition-colors"
                      data-testid={`picker-client-${c.id}`}>
                <div className="font-medium text-sm">{formatName(c.first_name)} {formatName(c.last_name)}</div>
                <div className="text-xs text-muted-foreground font-mono">{c.email || "—"}</div>
              </button>
            ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────────────── Section header ─────────────────────────── */
function SectionHeader({ title, subtitle, count, onNew, testId }) {
  return (
    <div className="flex items-center justify-between px-1 pb-3" data-testid={testId}>
      <div className="flex items-baseline gap-3">
        <h2 className="font-display font-extrabold text-3xl tracking-tight">{title}</h2>
        {subtitle && <span className="text-base text-muted-foreground">{subtitle}</span>}
        {count != null && <span className="text-xs label-uppercase text-muted-foreground">· {count}</span>}
      </div>
      {onNew && (
        <Button onClick={onNew} size="sm" className="gap-1.5" data-testid={`${testId}-new-btn`}>
          <Plus className="w-3.5 h-3.5" /> Neu
        </Button>
      )}
    </div>
  );
}

/* ─────────────────────────── Table component ─────────────────────────── */
const TH_CLS = "px-3 py-2 text-left label-uppercase text-muted-foreground text-[10px] font-medium whitespace-nowrap";
const TD_CLS = "px-3 py-2.5 text-sm align-middle";

function PolicenTable({ rows, clientNameFor, getPaymentColor, onOpen, ownerNameMap, getCommission }) {
  if (rows.length === 0) {
    return <div className="p-10 text-center text-sm text-muted-foreground">Keine Policen vorhanden</div>;
  }
  return (
    <table className="w-full text-sm">
      <thead className="bg-secondary/40 border-b border-border">
        <tr>
          <th className={TH_CLS}>Erstellt am</th>
          <th className={TH_CLS}>PolicenNr.</th>
          <th className={TH_CLS}>Typ</th>
          <th className={TH_CLS}>Gesellschaft</th>
          <th className={TH_CLS}>Beginn</th>
          <th className={TH_CLS}>Ablauf</th>
          <th className={`${TH_CLS} text-right`}>Prämie</th>
          <th className={TH_CLS}>Kunde</th>
          <th className={`${TH_CLS} text-right`}>Brutto-Prov.</th>
          <th className={`${TH_CLS} text-right`}>Netto-Prov.</th>
          <th className={TH_CLS}>Status</th>
          <th className={TH_CLS}>Zahlung</th>
          <th className={TH_CLS} />
        </tr>
      </thead>
      <tbody>
        {rows.map((p, idx) => {
          const { gross, net } = getCommission(p.id);
          return (
            <tr
              key={p.id}
              onClick={() => onOpen(p)}
              className={`border-b border-border hover:bg-secondary/40 transition-colors cursor-pointer animate-fade-up stagger-${Math.min(idx + 1, 12)}`}
              data-testid={`policy-row-${p.id}`}
            >
              <td className={`${TD_CLS} font-mono text-[11px] text-muted-foreground whitespace-nowrap`}>
                {p.created_at ? fmtDate(p.created_at) : "—"}
              </td>
              <td className={`${TD_CLS} font-mono text-prime whitespace-nowrap`}>{p.policy_number || "—"}</td>
              <td className={TD_CLS}>
                <span className="inline-flex items-center gap-1.5">
                  <Tag className="w-3.5 h-3.5 text-muted-foreground" />
                  {INSURANCE_TYPE_SHORT[p.insurance_type] || p.insurance_type}
                </span>
              </td>
              <td className={TD_CLS}>{p.company || "—"}</td>
              <td className={`${TD_CLS} font-mono text-[11px] text-muted-foreground whitespace-nowrap`}>{p.start_date ? fmtDate(p.start_date) : "—"}</td>
              <td className={`${TD_CLS} font-mono text-[11px] text-muted-foreground whitespace-nowrap`}>{p.end_date ? fmtDate(p.end_date) : "—"}</td>
              <td className={`${TD_CLS} font-mono font-medium text-right whitespace-nowrap`}>{fmtChf(p.premium)}</td>
              <td className={TD_CLS}>
                <div className="font-medium">{clientNameFor(p.client_id)}</div>
                {ownerNameMap[p.owner_id] && <div className="text-[10px] text-muted-foreground">{ownerNameMap[p.owner_id]}</div>}
              </td>
              <td className={`${TD_CLS} font-mono text-right whitespace-nowrap text-prime`}>
                {gross != null ? fmtChf(gross) : "—"}
              </td>
              <td className={`${TD_CLS} font-mono text-right whitespace-nowrap font-medium`}>
                {net != null ? fmtChf(net) : "—"}
              </td>
              <td className={TD_CLS}>
                <div className="flex items-center gap-1.5">
                  <PolicyStatusDot status={p.status || "bozza"} />
                  {p.customer_status && <CustomerStatusDot status={p.customer_status} />}
                </div>
              </td>
              <td className={TD_CLS}><PaymentDot color={getPaymentColor(p)} /></td>
              <td className={`${TD_CLS} text-muted-foreground text-right`}>
                <MoreHorizontal className="w-4 h-4 inline-block" />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function AntraegeTable({ rows, onOpen, ownerNameMap }) {
  if (rows.length === 0) {
    return <div className="p-10 text-center text-sm text-muted-foreground">Keine Anträge in Verarbeitung</div>;
  }
  return (
    <table className="w-full text-sm">
      <thead className="bg-secondary/40 border-b border-border">
        <tr>
          <th className={TH_CLS}>Erstellt am</th>
          <th className={TH_CLS}>Antragsnummer</th>
          <th className={TH_CLS}>Typ</th>
          <th className={TH_CLS}>Gesellschaft</th>
          <th className={TH_CLS}>Beginn</th>
          <th className={TH_CLS}>Ablauf</th>
          <th className={`${TH_CLS} text-right`}>Erw. Prämie</th>
          <th className={TH_CLS}>Provision</th>
          <th className={TH_CLS}>Kunde</th>
          <th className={TH_CLS}>Status</th>
          <th className={TH_CLS} />
        </tr>
      </thead>
      <tbody>
        {rows.map((a, idx) => (
          <tr
            key={a.id}
            onClick={() => onOpen(a)}
            className={`border-b border-border hover:bg-secondary/40 transition-colors cursor-pointer animate-fade-up stagger-${Math.min(idx + 1, 12)}`}
            data-testid={`antrag-row-${a.id}`}
          >
            <td className={`${TD_CLS} font-mono text-[11px] text-muted-foreground whitespace-nowrap`}>{a.created_at ? fmtDate(a.created_at) : "—"}</td>
            <td className={`${TD_CLS} font-mono text-prime whitespace-nowrap`}>{a.policy_number || a.id.slice(0, 8)}</td>
            <td className={TD_CLS}>
              <span className="inline-flex items-center gap-1.5">
                <Tag className="w-3.5 h-3.5 text-muted-foreground" />
                {INSURANCE_TYPE_SHORT[a.insurance_type] || a.insurance_type}
              </span>
            </td>
            <td className={TD_CLS}>{a.company || "—"}</td>
            <td className={`${TD_CLS} font-mono text-[11px] text-muted-foreground whitespace-nowrap`}>{(a.police_start_date || a.start_date) ? fmtDate(a.police_start_date || a.start_date) : "—"}</td>
            <td className={`${TD_CLS} font-mono text-[11px] text-muted-foreground whitespace-nowrap`}>{a.end_date ? fmtDate(a.end_date) : "—"}</td>
            <td className={`${TD_CLS} font-mono font-medium text-right whitespace-nowrap`}>{fmtChf(a.expected_premium)}</td>
            <td className={TD_CLS}>
              <ProvisionReadyBadge insuranceType={a.insurance_type} premium={a.expected_premium} details={a.details} testId={a.id} />
            </td>
            <td className={TD_CLS}>
              <div className="font-medium">{a.client_name || "—"}</div>
              {ownerNameMap[a.owner_id] && <div className="text-[10px] text-muted-foreground">{ownerNameMap[a.owner_id]}</div>}
            </td>
            <td className={TD_CLS}>
              <div className="flex items-center gap-1.5">
                <AntragStatusDot status={a.status} deadline={a.deadline} />
                {a.customer_status && <CustomerStatusDot status={a.customer_status} />}
              </div>
            </td>
            <td className={`${TD_CLS} text-muted-foreground text-right`}>
              <MoreHorizontal className="w-4 h-4 inline-block" />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ─────────────────────────── Main page ─────────────────────────── */
const VIEW_OPTIONS = [
  { key: "all",      label: "Alle",        icon: ListFilter },
  { key: "policen",  label: "Nur Policen", icon: FileText },
  { key: "antraege", label: "Nur Anträge", icon: Briefcase },
];
const MONTH_OPTIONS = [
  { key: "all",  label: "Alle Zeiten" },
  { key: "30",   label: "Letzte 30 Tage" },
  { key: "90",   label: "Letzte 90 Tage" },
  { key: "ytd",  label: "Seit Jahresbeginn" },
  { key: "12",   label: "Letzte 12 Monate" },
];

export default function AntraegeBoardPage() {
  const navigate = useNavigate();
  const [view, setView] = useState("all");
  const [policies, setPolicies] = useState([]);
  const [antrageBoard, setAntrageBoard] = useState(null);
  const [clientMap, setClientMap] = useState({});
  const [commissionsByPolicy, setCommissionsByPolicy] = useState({});
  const [consultants, setConsultants] = useState([]); // [{id, full_name}]
  const [loading, setLoading] = useState(true);
  const [picker, setPicker] = useState(null);
  // Filters
  const [filterBerater, setFilterBerater] = useState("all"); // user_id or "all"
  const [filterRange, setFilterRange] = useState("all");      // key from MONTH_OPTIONS

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, bRes, cRes, comRes, uRes] = await Promise.all([
        api.get("/policies"),
        api.get("/antraege-board"),
        api.get("/clients"),
        api.get("/commissions", { params: { scope: "team" } }).catch(() =>
          api.get("/commissions", { params: { scope: "mine" } }).catch(() => ({ data: [] }))),
        api.get("/users").catch(() => ({ data: [] })),
      ]);
      const polSorted = (pRes.data || []).slice().sort((a, b) =>
        (b.created_at || b.start_date || "").localeCompare(a.created_at || a.start_date || ""));
      setPolicies(polSorted);
      setAntrageBoard(bRes.data);
      const map = {};
      (cRes.data || []).forEach((c) => { map[c.id] = c; });
      setClientMap(map);
      const byPol = {};
      (comRes.data || []).forEach((c) => {
        if (!c.policy_id) return;
        if (!byPol[c.policy_id]) byPol[c.policy_id] = [];
        byPol[c.policy_id].push(c);
      });
      setCommissionsByPolicy(byPol);
      setConsultants((uRes.data || []).map((u) => ({
        id: u.id,
        full_name: u.full_name || `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email,
      })));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Laden");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const inProgressAntraege = useMemo(() => {
    if (!antrageBoard?.columns) return [];
    const cols = antrageBoard.columns;
    return [...(cols.kritisch || []), ...(cols.in_bearbeitung || []), ...(cols.approved || [])]
      .slice()
      .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  }, [antrageBoard]);

  /* ─────────────────────────── Filter pipeline ─────────────────────────── */
  const rangeStart = useMemo(() => {
    if (filterRange === "all") return null;
    const now = new Date();
    if (filterRange === "ytd") return new Date(Date.UTC(now.getUTCFullYear(), 0, 1)).toISOString();
    const days = Number(filterRange === "12" ? 365 : filterRange);
    return new Date(now.getTime() - days * 86400000).toISOString();
  }, [filterRange]);

  const applyFilters = useCallback((items) => items.filter((it) => {
    if (filterBerater !== "all" && it.owner_id !== filterBerater) return false;
    if (rangeStart && (it.created_at || "") < rangeStart) return false;
    return true;
  }), [filterBerater, rangeStart]);

  const filteredPolicies = useMemo(() => applyFilters(policies), [policies, applyFilters]);
  const filteredAntraege = useMemo(() => applyFilters(inProgressAntraege), [inProgressAntraege, applyFilters]);

  const showPolicen = view === "all" || view === "policen";
  const showAntraege = view === "all" || view === "antraege";

  const ownerNameMap = useMemo(() => {
    const m = {};
    consultants.forEach((u) => { m[u.id] = u.full_name; });
    return m;
  }, [consultants]);

  const clientNameFor = (clientId) => {
    const c = clientMap[clientId];
    if (!c) return "—";
    return `${formatName(c.first_name)} ${formatName(c.last_name)}`;
  };

  const onPickClient = (type) => (client) => {
    const qs = type === "policy" ? "?tab=policies" : "?tab=antraege";
    navigate(`/clients/${client.id}${qs}`);
  };

  // Commission totals per policy:
  //   gross  = Σ gross_amount (Provvigione lorda)
  //   net    = Σ paid_amount (= gross × 90%, the 10% storno-reserve is held back)
  const getCommission = useCallback((policyId) => {
    const cs = commissionsByPolicy[policyId];
    if (!cs || cs.length === 0) return { gross: null, net: null };
    const gross = cs.reduce((s, c) => s + (c.gross_amount || 0), 0);
    const net   = cs.reduce((s, c) => s + (c.paid_amount  || 0), 0);
    return { gross, net };
  }, [commissionsByPolicy]);

  /* ─────────────────────────── Export Excel ─────────────────────────── */
  const onExportExcel = () => {
    const wb = XLSX.utils.book_new();
    if (showPolicen && filteredPolicies.length > 0) {
      const rows = filteredPolicies.slice(0, 500).map((p) => {
        const { gross, net } = getCommission(p.id);
        return {
          "Erstellt am": p.created_at ? fmtDate(p.created_at) : "",
          "PolicenNr.": p.policy_number || "",
          "Typ": INSURANCE_TYPE_SHORT[p.insurance_type] || p.insurance_type,
          "Gesellschaft": p.company || "",
          "Beginn": p.start_date ? fmtDate(p.start_date) : "",
          "Ablauf": p.end_date ? fmtDate(p.end_date) : "",
          "Prämie (CHF)": p.premium || 0,
          "Kunde": clientNameFor(p.client_id),
          "Berater": ownerNameMap[p.owner_id] || "",
          "Brutto-Provision (CHF)": gross || 0,
          "Netto-Provision (CHF)": net || 0,
          "Status": POLICY_DOT_MAP[p.status]?.label || p.status,
          "Customer Status": CUSTOMER_DOT_MAP[p.customer_status]?.label || (p.customer_status || ""),
          "Zahlung": PAYMENT_LABEL_FOR_EXCEL(derivePaymentColor(p, commissionsByPolicy[p.id])),
        };
      });
      const ws = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(wb, ws, "Policen");
    }
    if (showAntraege && filteredAntraege.length > 0) {
      const rows = filteredAntraege.slice(0, 500).map((a) => ({
        "Erstellt am": a.created_at ? fmtDate(a.created_at) : "",
        "Antragsnummer": a.policy_number || a.id.slice(0, 8),
        "Typ": INSURANCE_TYPE_SHORT[a.insurance_type] || a.insurance_type,
        "Gesellschaft": a.company || "",
        "Beginn": (a.police_start_date || a.start_date) ? fmtDate(a.police_start_date || a.start_date) : "",
        "Ablauf": a.end_date ? fmtDate(a.end_date) : "",
        "Erw. Prämie (CHF)": a.expected_premium || 0,
        "Kunde": a.client_name || "",
        "Berater": ownerNameMap[a.owner_id] || "",
        "Status": a.status === "approved" ? "Genehmigt" : a.status === "rejected" ? "Abgelehnt" : "In Bearbeitung",
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(wb, ws, "Anträge");
    }
    if (wb.SheetNames.length === 0) {
      toast.error("Keine Daten zum Exportieren");
      return;
    }
    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `Antraege_Policen_${today}.xlsx`);
    toast.success("Excel-Datei heruntergeladen");
  };

  const onPrint = () => window.print();

  return (
    <div className="space-y-6" data-testid="antraege-policen-page">
      {/* ────────────────── Top toolbar ────────────────── */}
      <div className="flex items-end justify-between flex-wrap gap-3 animate-fade-up">
        <div>
          <div className="label-uppercase text-prime">/ Backoffice</div>
          <h1 className="font-display font-extrabold text-4xl lg:text-5xl tracking-tight mt-1">Anträge / Policen</h1>
          <p className="text-sm text-muted-foreground mt-1">Letzte Eintragungen — sortiert nach Datum (absteigend).</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap no-print">
          {/* Berater filter */}
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-border rounded-md bg-background">
            <Users className="w-3.5 h-3.5 text-muted-foreground" />
            <select
              value={filterBerater}
              onChange={(e) => setFilterBerater(e.target.value)}
              className="text-xs bg-transparent outline-none cursor-pointer min-w-[120px]"
              data-testid="filter-berater"
            >
              <option value="all">Alle Berater</option>
              {consultants.map((u) => (
                <option key={u.id} value={u.id}>{u.full_name}</option>
              ))}
            </select>
          </div>

          {/* Date range filter */}
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-border rounded-md bg-background">
            <select
              value={filterRange}
              onChange={(e) => setFilterRange(e.target.value)}
              className="text-xs bg-transparent outline-none cursor-pointer"
              data-testid="filter-range"
            >
              {MONTH_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* View segmented toggle */}
          <div className="inline-flex rounded-md border border-border overflow-hidden" data-testid="view-toggle">
            {VIEW_OPTIONS.map(({ key, label, icon: Icon }) => {
              const active = view === key;
              return (
                <button key={key} onClick={() => setView(key)}
                        className={`px-3 py-2 text-xs label-uppercase flex items-center gap-1.5 transition-colors ${
                          active ? "bg-foreground text-background" : "bg-background hover:bg-secondary"
                        }`}
                        data-testid={`view-${key}`}>
                  <Icon className="w-3.5 h-3.5" />{label}
                </button>
              );
            })}
          </div>

          <Button onClick={onPrint} variant="outline" size="sm" className="gap-1.5" data-testid="print-btn" title="Drucken">
            <Printer className="w-3.5 h-3.5" /> Drucken
          </Button>
          <Button onClick={onExportExcel} variant="outline" size="sm" className="gap-1.5" data-testid="export-excel-btn" title="Excel-Export">
            <Download className="w-3.5 h-3.5" /> Excel
          </Button>
          <Button onClick={load} variant="outline" size="sm" className="gap-1.5" disabled={loading} data-testid="board-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Aktualisieren
          </Button>
        </div>
      </div>

      {/* ────────────────── Policen ────────────────── */}
      {showPolicen && (
        <div className="space-y-2 animate-fade-up stagger-1">
          <SectionHeader title="Policen" count={filteredPolicies.length} onNew={() => setPicker("policy")} testId="policen-section" />
          <div className="prime-card overflow-x-auto">
            <PolicenTable
              rows={filteredPolicies.slice(0, 50)}
              clientNameFor={clientNameFor}
              ownerNameMap={ownerNameMap}
              getCommission={getCommission}
              getPaymentColor={(p) => derivePaymentColor(p, commissionsByPolicy[p.id])}
              onOpen={(p) => navigate(`/clients/${p.client_id}?tab=policies&policy=${p.id}`)}
            />
          </div>
        </div>
      )}

      {view === "all" && (
        <div className="h-1 bg-gradient-to-r from-transparent via-prime to-transparent rounded-full opacity-70 animate-fade-in no-print" aria-hidden="true" />
      )}

      {/* ────────────────── Anträge ────────────────── */}
      {showAntraege && (
        <div className="space-y-2 animate-fade-up stagger-2">
          <SectionHeader title="Anträge" subtitle="in Verarbeitung" count={filteredAntraege.length}
                         onNew={() => setPicker("antrag")} testId="antraege-section" />
          <div className="prime-card overflow-x-auto">
            <AntraegeTable
              rows={filteredAntraege.slice(0, 50)}
              ownerNameMap={ownerNameMap}
              onOpen={(a) => navigate(`/clients/${a.client_id}?tab=antraege&antrag=${a.id}`)}
            />
          </div>
        </div>
      )}

      <ClientPicker
        open={picker !== null}
        onClose={() => setPicker(null)}
        onPick={picker === "policy" ? onPickClient("policy") : onPickClient("antrag")}
        label={picker === "policy" ? "Neue Police anlegen" : "Neuen Antrag anlegen"}
      />
    </div>
  );
}
